// Full-screen scaffold for mobile wizards and settings-detail pages.
// Header (back arrow + title + optional `step/total` indicator) is
// fixed at the top; `footer` is sticky at the bottom with
// safe-area padding so the OS home indicator doesn't cover it.
// `children` scrolls between them.

import * as React from "react";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

interface MobileWizardShellProps {
  title: React.ReactNode;
  step?: { current: number; total: number };
  onBack?: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

export function MobileWizardShell({
  title,
  step,
  onBack,
  children,
  footer,
  className,
}: MobileWizardShellProps) {
  return (
    <div
      className={cn(
        "flex min-h-dvh flex-col bg-bg-app",
        className,
      )}
    >
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-border bg-bg-raised px-3 py-2">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="-ml-1 flex h-10 w-10 items-center justify-center rounded-md text-text-soft hover:bg-accent-bg hover:text-text"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        <div className="min-w-0 flex-1">
          {step && (
            <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-text-dim">
              Step {step.current} of {step.total}
            </div>
          )}
          <div className="truncate text-base font-semibold">{title}</div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-4">{children}</main>

      {footer && (
        <footer className="sticky bottom-0 z-10 border-t border-border bg-bg-raised px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+12px)]">
          {footer}
        </footer>
      )}
    </div>
  );
}
